import {AbstractLitElement} from "../abstract-lit.element";
import {property} from "lit/decorators/property.js";
import {html, PropertyValues} from "lit";
import {when} from "lit/directives/when.js";
import {map} from "lit/directives/map.js";

export abstract class AbstractInputComponent extends AbstractLitElement {

    @property({type: Boolean})
    public required: boolean = false;

    @property({type: String})
    public label: string | null = null;

    @property({type: Object})
    public errors: { [key: string]: string[] } = {};

    @property({type: Boolean})
    public readonly: boolean = false;

    /**
     * An explanatory hint shown UNDER the field — e.g. an accepted format, a size
     * limit or a unit. Purely informational and always visible once set (there is
     * deliberately NO `readonly` special case).
     *
     * This is NOT the validation output: error messages live in their own
     * `ul.input-error` block, are fed by `errors` and are styled separately.
     *
     * `p.input-helptext` is the house convention — the dialogs used to write that
     * markup by hand next to a field; `_renderHelperText()` now renders it once,
     * inside the field it belongs to.
     */
    @property({type: String, attribute: `helper-text`})
    public helperText: string | null = null;

    /**
     * Renders the hint markup. Every input component calls this from its own
     * `render()` so the markup exists exactly once, not fourteen times.
     */
    protected _renderHelperText(): unknown {
        return when(!!this.helperText, () => html`<p class="input-helptext">${this.helperText}</p>`);
    }

    /**
     * Renders the validation messages as `ul.input-error`, one list item per message.
     * `errors` maps a field key to its messages (bind-to.directive.ts writes it that
     * way), so the messages of all keys are listed. Every input component calls this
     * from its own `render()`.
     */
    protected _renderErrors(): unknown {
        const messages = Object.values(this.errors ?? {}).flat();
        return when(messages.length > 0, () => html`
            <ul class="input-error">${map(messages, message => html`<li>${message}</li>`)}</ul>
        `);
    }

    public fire<TData>(value: TData, eventName = 'value-changed'): void {
        this.dispatchEvent(new CustomEvent<{ value: TData }>(eventName, {detail: {value}}))
    }

    protected willUpdate(_changedProperties: PropertyValues) {
        super.willUpdate(_changedProperties);

        if (_changedProperties.has(`readonly`) && this.readonly) {
            this.classList.add(`readonly`);
        }
    }
}